/**
 * «Restos del modo simulado» (Configuración → Cuenta, C4 `restos-simulados`).
 *
 * Con el servidor en twilio desaparece el banner «Modo simulado» y lo que quedó de las pruebas se
 * ve suelto, con chips chicos: plantillas, campañas, bajas en «no contactar», datos de la cuenta. Esta
 * tarjeta los junta con su cantidad y, para el operador, ofrece limpiarlos con un motivo (queda en la
 * auditoría). Los mensajes, los movimientos y las respuestas no se borran: son historia, y ya quedan
 * fuera del saldo y de las estadísticas.
 *
 * De «no contactar» la limpieza levanta sólo las bajas con la marca `simulado: true`. Las del canal
 * oficial sin la marca (`noContactarSinMarca`) no las toca nunca: la tarjeta las avisa con un link a
 * No contactar para revisarlas a mano, y aparece por ellas aunque no haya otro resto. El botón sólo
 * aparece si la limpieza tiene algo que hacer (`hayQueLimpiar`).
 *
 * Sólo en twilio: en simulado lo simulado ES la demo, y ni se pregunta. Si no hay restos no aparece.
 * Es una tarjeta opcional: si el pedido falla (un backend sin el endpoint), no aparece.
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Eraser, FlaskConical, Loader2 } from "lucide-react";
import type { RestosSimulados, ResultadoLimpiezaRestos } from "../../types";
import { oficialApi } from "../../api";
import { useCuentaOficial } from "../../contexto";
import { leerError, leerRespuesta, textoError } from "../../logic/respuesta";
import { hayQueLimpiar, lineasRestos, TEXTO_QUE_HACE_LIMPIEZA, textoBajasSinMarca, textoLimpieza, textoRebase } from "../../logic/restos";
import { ModalMotivo } from "../ModalMotivo";
import { Tarjeta } from "../Tarjeta";
import { activaApi } from "../../../campana-nueva/api";
import { esEquipoDeenex } from "../../../campana-nueva/rolActiva";

const RUTA_NO_CONTACTAR = "/activa/oficial/bajas";

/** Texto de un rechazo de la limpieza: los códigos del contrato con texto propio, si no el del backend. */
function textoErrorLimpieza(x: { status: number; code?: string; error?: string }): string {
  if (x.error && x.error.trim()) return x.error.trim();
  if (x.code === "SOLO_TWILIO") return "Sólo se limpia con el servidor en Twilio: en simulado, lo simulado es la demo.";
  if (x.status === 400) return "Escribí el motivo de la limpieza.";
  return textoError(x);
}

export function TarjetaRestosSimulados() {
  const { modo, recargar } = useCuentaOficial();
  const enTwilio = modo === "twilio";
  const [restos, setRestos] = useState<RestosSimulados | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState("");
  const [hecho, setHecho] = useState("");

  const traer = useCallback(async () => {
    try {
      const l = leerRespuesta<RestosSimulados>(await oficialApi.restosSimulados());
      setRestos(l.ok && l.data ? l.data : null);
    } catch {
      setRestos(null);   // tarjeta opcional: sin datos, no aparece
    }
  }, []);

  useEffect(() => { if (enTwilio) void traer(); }, [enTwilio, traer]);

  const limpiar = async (motivo: string) => {
    setOcupado(true);
    setError("");
    try {
      const l = leerRespuesta<ResultadoLimpiezaRestos>(await oficialApi.limpiarRestosSimulados(motivo));
      if (!l.ok) { setError(textoErrorLimpieza(l)); return; }
      setHecho(textoLimpieza(l.data?.acciones, l.data));
      if (l.data?.despues) setRestos(l.data.despues); else await traer();
      await recargar();
    } catch (e) {
      setError(textoErrorLimpieza(leerError(e)));
    } finally {
      setOcupado(false);
      setAbierto(false);
    }
  };

  const sinMarca = textoBajasSinMarca(restos?.noContactarSinMarca);
  if (!enTwilio || !restos || (!restos.hayRestos && !sinMarca && !hecho)) return null;
  const lineas = lineasRestos(restos);
  const rebase = textoRebase(restos.rebase);
  const limpiable = hayQueLimpiar(restos);
  // La limpieza la hace el equipo de Deenex con su comercio elegido (no una persona de un cliente).
  const operador = activaApi.esOperador() && esEquipoDeenex();
  const sumanAlSaldo = (Number(restos.movimientos?.total) || 0) > (Number(restos.movimientos?.fueraDelSaldo) || 0);
  const sub = `Quedaron de las pruebas de antes de pasar a Twilio. Nada de esto sale a WhatsApp${sumanAlSaldo ? "." : " ni cuenta en el saldo."}`;

  return (
    <Tarjeta titulo="Restos del modo simulado" sub={sub}>
      <div className="space-y-3 text-sm">
        {restos.hayRestos && lineas.length > 0 && (
          <ul className="space-y-1 text-slate-700">
            {lineas.map((l) => (
              <li key={l} className="flex items-start gap-2"><FlaskConical size={14} className="text-amber-600 flex-shrink-0 mt-0.5" /> {l}</li>
            ))}
          </ul>
        )}
        {sinMarca && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
            <p className="flex items-start gap-2"><AlertTriangle size={14} className="flex-shrink-0 mt-0.5" /> {sinMarca}</p>
            <Link to={RUTA_NO_CONTACTAR} className="mt-1 inline-block text-xs font-semibold underline">Ir a Bajas</Link>
          </div>
        )}
        {rebase && <p className="text-slate-600">{rebase}</p>}
        {hecho && (
          <p role="status" data-testid="restos-resultado" className="flex items-start gap-1.5 text-emerald-700">
            <CheckCircle2 size={15} className="flex-shrink-0 mt-0.5" /> {hecho}
          </p>
        )}
        {error && <p role="alert" className="text-red-600">{error}</p>}
        {limpiable && (operador ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">{TEXTO_QUE_HACE_LIMPIEZA}</p>
            <button type="button" onClick={() => { setError(""); setAbierto(true); }} disabled={ocupado}
              className="inline-flex items-center gap-1.5 rounded-xl border border-amber-200 bg-white px-3.5 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-50 disabled:opacity-50">
              {ocupado ? <Loader2 size={15} className="animate-spin" /> : <Eraser size={15} />} Limpiar restos del simulado
            </button>
          </div>
        ) : (
          <p className="text-xs text-slate-500">Los limpia un operador de Deenex (entrando en modo operador).</p>
        ))}
      </div>
      {abierto && (
        <ModalMotivo
          titulo="Limpiar restos del simulado"
          descripcion={TEXTO_QUE_HACE_LIMPIEZA}
          confirmar="Limpiar"
          peligro
          busy={ocupado}
          onConfirmar={(m) => void limpiar(m)}
          onCancelar={() => setAbierto(false)}
        />
      )}
    </Tarjeta>
  );
}

export default TarjetaRestosSimulados;
